"use client"

import { useEffect, useState, useRef } from "react"
import { Wifi, WifiOff, Download, Search, UserPlus, HelpCircle, AlertTriangle, Tag, RefreshCw, Upload } from "lucide-react"
import DynamicTable from "./DynamicTable"
import DynamicRegisterModal from "./DynamicRegisterModal"
import DynamicPrintModal from "./DynamicPrintModal"
import DynamicBulkUploadModal from "./DynamicBulkUploadModal"
import LoadingBar from "./LoadingBar"
import Toast from "./Toast"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { useQueueManager } from "../hooks/useQueueManager"
import { useDynamicUsers } from "../hooks/useDynamicUsers"
import { useToast } from "../hooks/useToast"
import { useTourGuide } from "../hooks/useTourGuide"
import { setApiOperationCallback } from "../services/api"
import { clearAllData } from "../services/database"
import type { DynamicUser } from "../types/dynamic"
import { extractFolioFromScan } from "../utils/scanSearchTerm"

function MainPanel() {
  const isOnline = useNetworkStatus()
  
  // Dynamic system hooks - SOLO SISTEMA DINÁMICO
  const {
    users: dynamicUsers,
    schema: dynamicSchema,
    config: dynamicConfig,
    loading: dynamicLoading,
    error: dynamicError,
    refreshData: refreshDynamicData,
    cancelCurrentFetch: cancelDynamicFetch,
    setApiOperationActive: setDynamicApiOperationActive,
    getFieldValue,
    formatFieldValue,
    filterUsers,
    addUserOptimistically,
    updateUserOptimistically,
    replaceUserOptimistically,
  } = useDynamicUsers()
  
  const {
    queueCount,
    isProcessing,
    lastSync,
    error: queueError,
  } = useQueueManager(() => {
    // Callback to refresh data after queue processing
    refreshDynamicData()
  }, () => {}) // No setQueueProcessing needed
  
  const { toasts, showToast, removeToast } = useToast()
  const { startTour, isActive: isTourActive } = useTourGuide()
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearchLocked, setIsSearchLocked] = useState(false)
  const [lockedSearchTerm, setLockedSearchTerm] = useState("")
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [confirmationText, setConfirmationText] = useState("")
  const [showGenericPrintModal, setShowGenericPrintModal] = useState(false)
  
  // Referencia al input de búsqueda para la funcionalidad de búsqueda global
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [windowInFocus, setWindowInFocus] = useState(true)

  // Track window focus for global search
  useEffect(() => {
    const handleFocus = () => setWindowInFocus(true)
    const handleBlur = () => setWindowInFocus(false)

    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  // Configure API operation tracking
  useEffect(() => {
    setApiOperationCallback(setDynamicApiOperationActive)
  }, [setDynamicApiOperationActive])

  // Función para detectar si hay modales abiertos de forma escalable
  const hasOpenModals = () => {
    // Detectar modales por múltiples estrategias para ser completamente replicable
    
    // 1. Verificar modales conocidos por estado
    if (showRegisterModal || showResetModal || showGenericPrintModal) return true
    
    // 2. Detectar modales por clases CSS comunes (overlay + modal)
    const modalOverlays = document.querySelectorAll('.fixed.inset-0, [class*="modal"], [class*="overlay"]')
    if (modalOverlays.length > 0) return true
    
    // 3. Detectar elementos con z-index alto que cubran la pantalla
    const highZIndexElements = Array.from(document.querySelectorAll('*')).filter(el => {
      const computedStyle = window.getComputedStyle(el as Element)
      const zIndex = parseInt(computedStyle.zIndex) || 0
      const position = computedStyle.position
      
      // Buscar elementos con z-index >= 40 y position fixed/absolute que cubran gran parte de la pantalla
      if (zIndex >= 40 && (position === 'fixed' || position === 'absolute')) {
        const rect = (el as Element).getBoundingClientRect()
        const coversScreen = rect.width > window.innerWidth * 0.5 && rect.height > window.innerHeight * 0.5
        return coversScreen
      }
      return false
    })
    
    if (highZIndexElements.length > 0) return true
    
    // 4. Detectar driver.js (tour guide) activo
    const driverElements = document.querySelectorAll('.driver-active-element, .driver-popover, [class*="driver"]')
    if (driverElements.length > 0) return true
    
    return false
  }

  // Funciones para manejar búsqueda bloqueada
  const lockSearch = () => {
    if (searchTerm.trim()) {
      setLockedSearchTerm(extractFolioFromScan(searchTerm.trim()))
      setIsSearchLocked(true)
      setSearchTerm("") // Limpiar el campo pero mantener los resultados
      if (searchInputRef.current) {
        searchInputRef.current.blur() // Quitar foco para que se vea el placeholder
      }
    }
  }

  const unlockSearch = () => {
    setIsSearchLocked(false)
    setLockedSearchTerm("")
    setSearchTerm("")
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  const clearSearch = () => {
    setSearchTerm("")
    setIsSearchLocked(false)
    setLockedSearchTerm("")
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  // Colapsa espacios y recorta
  const normalizeSearch = (search: string) => search.replace(/\s+/g, " ").trim()

  // Función para obtener el término de búsqueda efectivo
  const getEffectiveSearchTerm = () => {
    //return isSearchLocked ? lockedSearchTerm : searchTerm
    const term = isSearchLocked ? lockedSearchTerm : searchTerm
    return normalizeSearch(extractFolioFromScan(term))
  }

  // Global search functionality - focus search input when typing
  useEffect(() => {
    const handleGlobalKeydown = (event: KeyboardEvent) => {
      // Condiciones para NO interceptar el teclado:
      
      // 1. Si la ventana no está en foco
      if (!windowInFocus) return
      
      // 2. Si hay algún modal abierto (detección escalable)
      if (hasOpenModals()) return
      
      // 3. Si el tour guide está activo
      if (isTourActive) return
      
      // 4. Si el foco está en otro input, textarea o elemento editable
      const activeElement = document.activeElement
      if (activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        (activeElement as HTMLElement).isContentEditable
      )) {
        // Excepción: si estamos en el input de búsqueda, permitir Delete/Backspace
        if (activeElement === searchInputRef.current && (event.key === 'Backspace' || event.key === 'Delete')) {
          return // Dejar que el comportamiento normal del input maneje esto
        }
        return
      }
      
      // 5. Si se presionaron teclas especiales (Ctrl, Alt, Meta, etc.)
      if (event.ctrlKey || event.altKey || event.metaKey) return
      
      // 6. Manejar Enter para bloquear búsqueda
      if (event.key === 'Enter') {
        if (activeElement === searchInputRef.current && searchTerm.trim()) {
          lockSearch()
          event.preventDefault()
        }
        return
      }
      
      // 7. Si es una tecla especial
      const specialKeys = [
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Escape', 'Tab',
        'Home', 'End', 'PageUp', 'PageDown', 'F1', 'F2', 'F3', 'F4', 'F5',
        'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
      ]
      if (specialKeys.includes(event.key)) return
      
      // 8. Manejar Delete/Backspace para borrar en el campo de búsqueda
      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
          
          if (event.key === 'Backspace') {
            // Borrar último carácter
            setSearchTerm(prev => prev.slice(0, -1))
          } else if (event.key === 'Delete') {
            // Limpiar todo el campo de búsqueda
            setSearchTerm('')
          }
          
          event.preventDefault()
        }
        return
      }
      
      // 9. Si llegamos aquí, es una tecla normal (letra, número, etc.)
      // Enfocar el input de búsqueda y agregar el carácter
      if (searchInputRef.current && event.key.length === 1) {
        // Si la búsqueda está bloqueada y escribimos algo nuevo, desbloquear primero
        if (isSearchLocked) {
          unlockSearch()
        }
        
        searchInputRef.current.focus()
        
        // Si es una letra/número/símbolo, agregarlo al término de búsqueda
        // ({ } " : , vienen del QR del gafete escaneado)
        if (event.key.match(/^[a-zA-Z0-9\s@.{}":,-]$/)) {
          setSearchTerm(prev => prev + event.key)
          // Prevenir que el carácter se escriba dos veces
          event.preventDefault()
        }
      }
    }

    // Agregar el event listener
    document.addEventListener('keydown', handleGlobalKeydown)

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleGlobalKeydown)
    }
  }, [showRegisterModal, showResetModal, showGenericPrintModal, isTourActive, windowInFocus])

  // Show toast notifications for queue errors
  useEffect(() => {
    if (queueError) {
      showToast("error", `Error en sincronización: ${queueError}`)
    }
  }, [queueError, showToast])

  // Los avisos los muestran los modales (registro, edición, impresión) según el resultado de la sincronización
  const handleDynamicRegisterSuccess = (newUser: DynamicUser) => {
    // Use hook's optimistic update function
    addUserOptimistically(newUser)
  }

  const handleDynamicEditSuccess = (updatedUser: DynamicUser) => {
    // Use hook's optimistic update function
    updateUserOptimistically(updatedUser)
  }

  const handleDynamicBulkUploadSuccess = (newUsers: DynamicUser[]) => {
    // Agregar usuarios usando la función optimista
    newUsers.forEach(user => addUserOptimistically(user))
    showToast("success", `${newUsers.length} usuarios cargados masivamente`)
  }

  // Crear usuario genérico para etiqueta
  const createGenericUser = (): DynamicUser => {
    const genericUser: DynamicUser = {
      id: 0, // ID temporal para etiqueta genérica
      name: '',
      last_name: '',
      email: '',
      phone_number: '',
      printed: 0,
      extra: {}
    }

    // Inicializar campos extra con valores vacíos
    Object.keys(dynamicSchema || {}).forEach(fieldName => {
      const fieldSchema = dynamicSchema![fieldName]
      if (fieldSchema.type === 'boolean') {
        genericUser.extra[fieldName] = false
      } else if (fieldSchema.type === 'number') {
        genericUser.extra[fieldName] = 0
      } else {
        genericUser.extra[fieldName] = ''
      }
    })

    return genericUser
  }

  const getSyncStatus = () => {
    if (isProcessing) return "Sincronizando..."
    if (queueCount > 0) return `${queueCount} pendientes`
    if (lastSync || isOnline) return "Sincronizado"
    return "Sin sincronizar"
  }

  const getSyncStatusGlassColor = () => {
    if (isProcessing) return "bg-yellow-500/20 border-yellow-300/30 text-yellow-800"
    if (queueCount > 0) return "bg-orange-500/20 border-orange-300/30 text-orange-800"
    if (lastSync || isOnline) return "bg-green-500/20 border-green-300/30 text-green-800"
    return "bg-gray-500/20 border-gray-300/30 text-gray-800"
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <LoadingBar isLoading={dynamicLoading || isProcessing} />

      {/* Header */}
      <header id="navbar" className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-semibold text-gray-900">FEMECOT 2026</h1>
            </div>

            <div className="flex items-center space-x-3">

              {/* Tour Guide Button */}
              <button
                onClick={startTour}
                disabled={isTourActive}
                className="flex items-center px-3 py-1 bg-purple-500/20 backdrop-blur-sm border border-purple-300/30 text-purple-800 rounded-xl text-sm font-medium hover:bg-purple-500/30 hover:border-purple-400/40 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 disabled:opacity-50"
                aria-label="Iniciar guía de uso"
              >
                <HelpCircle className="h-4 w-4 mr-1" aria-hidden="true" />
                Guía de uso
              </button>

              {/* Sync Status */}
              <span className={`px-3 py-1 rounded-xl text-sm font-medium backdrop-blur-sm border transition-all duration-300 ${getSyncStatusGlassColor()}`}>
                {getSyncStatus()}
              </span>

              {/* Network Status and Upload Button */}
              <div className="flex items-center space-x-2">
                {isOnline ? (
                  <div className="flex items-center px-3 py-1 bg-blue-500/20 backdrop-blur-sm border border-blue-300/30 text-blue-800 rounded-xl text-sm font-medium">
                    <Wifi className="h-4 w-4 mr-1" aria-hidden="true" />
                    En línea
                  </div>
                ) : (
                  <div className="flex items-center px-3 py-1 bg-red-500/20 backdrop-blur-sm border border-red-300/30 text-red-800 rounded-xl text-sm font-medium">
                    <WifiOff className="h-4 w-4 mr-1" aria-hidden="true" />
                    Sin conexión
                  </div>
                )}
                
                {/* Bulk Upload Button */}
                <button
                  onClick={() => setShowBulkUploadModal(true)}
                  disabled={!dynamicSchema}
                  className="flex items-center px-3 py-1 bg-orange-500/20 backdrop-blur-sm border border-orange-300/30 text-orange-800 rounded-xl text-sm font-medium hover:bg-orange-500/30 hover:border-orange-400/40 hover:shadow-lg hover:shadow-orange-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Carga masiva de usuarios"
                  title="Carga masiva de usuarios"
                >
                  <Upload className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Search and Actions Bar */}
        <div className="flex justify-between items-center mb-6">
          <div id="search-bar" className="flex-1 max-w-md">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={
                  isSearchLocked 
                    ? `Buscando: ${lockedSearchTerm}` 
                    : "Buscar usuarios..."
                }
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  // Si estaba bloqueada y empezamos a escribir, desbloquear
                  if (isSearchLocked && e.target.value) {
                    setIsSearchLocked(false)
                    setLockedSearchTerm("")
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    lockSearch()
                  }
                }}
                className={`w-full pl-10 pr-12 py-2 bg-white border-2 text-gray-800 rounded-xl outline-none transition-all duration-300 placeholder-gray-500 ${
                  isSearchLocked
                    ? "border-orange-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:bg-orange-50"
                    : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-blue-50"
                }`}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 transition-colors duration-300 ${
                  isSearchLocked ? "text-orange-500" : "text-gray-500"
                }`} aria-hidden="true" />
              </div>
              
              {/* Botón X para limpiar búsqueda */}
              {(searchTerm.length > 0 || isSearchLocked) && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  title="Limpiar búsqueda"
                  aria-label="Limpiar búsqueda"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex space-x-3 ml-4">
            <button
              id="new-assistant-btn"
              onClick={() => setShowRegisterModal(true)}
              disabled={!dynamicSchema}
              className="inline-flex items-center px-4 py-2 bg-green-600/20 backdrop-blur-sm border border-green-400/30 text-green-800 text-sm font-medium rounded-xl hover:bg-green-600/30 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="h-4 w-4 mr-2" aria-hidden="true" />
              Nuevo Asistente
            </button>

            <button
              id="generic-label-btn"
              onClick={() => setShowGenericPrintModal(true)}
              disabled={!dynamicSchema}
              className="inline-flex items-center px-4 py-2 bg-purple-600/20 backdrop-blur-sm border border-purple-400/30 text-purple-800 text-sm font-medium rounded-xl hover:bg-purple-600/30 hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Tag className="h-4 w-4 mr-2" aria-hidden="true" />
              Etiqueta Genérica
            </button>

            <button
              id="refresh-btn"
              onClick={() => refreshDynamicData()}
              disabled={dynamicLoading}
              className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-800 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {dynamicLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4 mr-2" aria-hidden="true" />
              )}
              Actualizar
            </button>

            <button
              id="reset-btn"
              onClick={() => setShowResetModal(true)}
              disabled={queueCount > 0 || isProcessing}
              className="inline-flex items-center px-4 py-2 bg-red-600/20 backdrop-blur-sm border border-red-400/30 text-red-800 text-sm font-medium rounded-xl hover:bg-red-600/30 hover:border-red-500/40 hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title={queueCount > 0 ? `No se puede resetear: hay ${queueCount} procesos pendientes` : "Reset completo del sitio"}
              aria-label={queueCount > 0 ? `Reset bloqueado (${queueCount} procesos pendientes)` : "Iniciar reset completo"}
            >
              <AlertTriangle className="h-4 w-4 mr-2" aria-hidden="true" />
              Reset Completo
            </button>
          </div>
        </div>

        {/* Table */}
        <div id="table-container">
          <DynamicTable
            users={dynamicUsers}
            schema={dynamicSchema}
            config={dynamicConfig}
            loading={dynamicLoading}
            error={dynamicError}
            showToast={showToast}
            searchTerm={getEffectiveSearchTerm()}
            cancelCurrentFetch={cancelDynamicFetch}
            getFieldValue={getFieldValue}
            formatFieldValue={formatFieldValue}
            filterUsers={filterUsers}
            onEditSuccess={handleDynamicEditSuccess}
            loadingStep="Cargando sistema dinámico..."
          />
        </div>
      </main>

      {/* Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-500 mr-2" aria-hidden="true" />
              <h2 className="text-lg font-semibold">ADVERTENCIA: RESET COMPLETO</h2>
            </div>
            <div className="mb-4">
              Esta acción eliminará PERMANENTEMENTE:
              <ul className="list-disc pl-5 mt-2">
                <li>Todos los datos guardados localmente</li>
                <li>Caché de la aplicación</li>
                <li>Base de datos IndexedDB</li>
                <li>Configuraciones locales</li>
              </ul>
              <p className="mt-3 text-green-600 font-medium">
                Los datos almacenados en la nube NO se verán afectados por esta operación.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Escribe "RESTAURAR" para confirmar:
              </label>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                className="w-full bg-white border-2 border-gray-300 text-gray-800 rounded-xl px-3 py-2 focus:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-300 placeholder-gray-500"
                placeholder="RESTAURAR"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowResetModal(false)
                  setConfirmationText("")
                }}
                className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (confirmationText === "RESTAURAR") {
                    // Limpiar cachés
                    const cacheNames = await caches.keys()
                    await Promise.all(cacheNames.map(name => caches.delete(name)))
                    
                    // Limpiar IndexedDB
                    await clearAllData()
                    
                    // Limpiar localStorage
                    localStorage.clear()
                    
                    // Unregister Service Worker
                    const registrations = await navigator.serviceWorker.getRegistrations()
                    await Promise.all(registrations.map(r => r.unregister()))
                    
                    // Recargar
                    window.location.reload()
                  }
                }}
                disabled={confirmationText !== "RESTAURAR"}
                className="px-4 py-2 bg-red-600/20 backdrop-blur-sm border border-red-400/30 text-red-800 rounded-xl hover:bg-red-600/30 hover:border-red-500/40 hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                SÍ, RESTAURAR TODO
              </button>
            </div>
          </div>
        </div>
      )}

      {showRegisterModal && (
        <DynamicRegisterModal
          schema={dynamicSchema}
          existingUsers={dynamicUsers}
          onClose={() => setShowRegisterModal(false)}
          onSuccess={handleDynamicRegisterSuccess}
          onSynced={replaceUserOptimistically}
          showToast={showToast}
          cancelCurrentFetch={cancelDynamicFetch}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && dynamicSchema && (
        <DynamicBulkUploadModal
          schema={dynamicSchema}
          existingUsers={dynamicUsers}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={handleDynamicBulkUploadSuccess}
          onServerCreated={() => refreshDynamicData()}
          showToast={showToast}
          cancelCurrentFetch={cancelDynamicFetch}
        />
      )}

      {/* Generic Print Modal */}
      {showGenericPrintModal && dynamicSchema && (
        <DynamicPrintModal
          user={createGenericUser()}
          schema={dynamicSchema}
          onClose={() => setShowGenericPrintModal(false)}
          onPrintSuccess={() => {
            // No necesita hacer nada especial para etiquetas genéricas
            showToast("success", "Etiqueta genérica impresa correctamente")
          }}
          showToast={showToast}
          cancelCurrentFetch={cancelDynamicFetch}
        />
      )}

      {/* Toast Notifications */}
      {toasts.map((toast, index) => (
        <Toast 
          key={toast.id} 
          type={toast.type} 
          message={toast.message} 
          onClose={() => removeToast(toast.id)} 
          index={index}
        />
      ))}
    </div>
  )
}

export default MainPanel
